/**
 * selectionView — WHAT is selected, not just WHICH ids.
 *
 * The grid store's selection is flat: `{ gridId, itemIds }`. The editor's
 * canvas is not: a page is a grid of section blocks, a section is a grid of
 * widgets, and the same store shape describes both levels. Every selection
 * surface (the action bar, the keyboard verbs) needs the level to decide which
 * verbs make sense — "crop" for an image, "gap" for a section, nothing for a
 * multi-selection but delete — so the classification lives here, once, as a
 * pure function over store state (testable without a render).
 *
 *   widget  — one item that is not a section block. If its grid is a section's
 *             nested grid the parent block is resolved too (the "select the
 *             section" step of hierarchical selection).
 *   section — one `contentType: "grid"` block (a page-grid item that hosts a
 *             nested grid).
 *   inline  — one `contentType: "inline-grid"` block: a container too (a
 *             one-row grid inside a section), but not a section — its blocks
 *             are ordinary widgets whose `parent` is the Inline.
 *   multi   — two or more items (always in one grid; the store guarantees it).
 *   none    — nothing selected.
 */
import { useMemo } from "react";
import { useGridStore } from "@fredericrous/grid";
import type { GridItem, GridState } from "@fredericrous/grid";
import type { InlineJustify } from "@website-builder/schemas";

import { justifyOf } from "./inlineRows.js";

export interface WidgetSelection {
  readonly kind: "widget";
  readonly gridId: string;
  readonly itemId: string;
  readonly item: GridItem;
  readonly contentType: string;
  /** The section block hosting this widget's grid, when there is one. */
  readonly parent: { readonly gridId: string; readonly itemId: string } | null;
}

export interface SectionSelection {
  readonly kind: "section";
  /** The page grid the block sits in. */
  readonly gridId: string;
  readonly itemId: string;
  readonly item: GridItem;
  /** The section's own grid (its widgets). */
  readonly nestedGridId: string | null;
  readonly label: string;
}

export interface InlineSelection {
  readonly kind: "inline";
  /** The section grid the Inline sits in. */
  readonly gridId: string;
  readonly itemId: string;
  readonly item: GridItem;
  /** The Inline's own one-row grid (its blocks). */
  readonly nestedGridId: string | null;
  readonly justify: InlineJustify;
}

export interface MultiSelection {
  readonly kind: "multi";
  readonly gridId: string;
  readonly itemIds: readonly string[];
  readonly parent: { readonly gridId: string; readonly itemId: string } | null;
}

export type SelectionView =
  | { readonly kind: "none" }
  | WidgetSelection
  | SectionSelection
  | InlineSelection
  | MultiSelection;

const NONE: SelectionView = { kind: "none" };

type Grids = Readonly<Record<string, GridState>>;
type Selection = { gridId: string; itemIds: string[] } | null;

function parentOf(
  grid: GridState | undefined,
): { gridId: string; itemId: string } | null {
  if (!grid?.parentGridId || !grid.parentItemId) return null;
  return { gridId: grid.parentGridId, itemId: grid.parentItemId };
}

/** Pure classification — see the module header. */
export function classifySelection(
  grids: Grids,
  selection: Selection,
): SelectionView {
  if (!selection) return NONE;
  const grid = grids[selection.gridId];
  if (!grid) return NONE;
  const ids = selection.itemIds.filter((id) => grid.items[id] != null);
  if (ids.length === 0) return NONE;
  if (ids.length > 1) {
    return {
      kind: "multi",
      gridId: selection.gridId,
      itemIds: ids,
      parent: parentOf(grid),
    };
  }
  const itemId = ids[0]!;
  const item = grid.items[itemId]!;
  if (item.contentType === "grid") {
    const nested =
      item.nestedGridId ??
      (item.contentProps?.["nestedGridId"] as string | undefined) ??
      null;
    const label =
      (item.contentProps?.["label"] as string | undefined) ?? "Section";
    return {
      kind: "section",
      gridId: selection.gridId,
      itemId,
      item,
      nestedGridId: nested,
      label,
    };
  }
  if (item.contentType === "inline-grid") {
    return {
      kind: "inline",
      gridId: selection.gridId,
      itemId,
      item,
      nestedGridId:
        item.nestedGridId ??
        (item.contentProps?.["nestedGridId"] as string | undefined) ??
        null,
      justify: justifyOf(item.contentProps),
    };
  }
  return {
    kind: "widget",
    gridId: selection.gridId,
    itemId,
    item,
    contentType: item.contentType,
    parent: parentOf(grid),
  };
}

/**
 * Reactive classification. Subscribes to the selection and the one grid it
 * points at (both stable references between unrelated store updates), and
 * memoises the derived object so consumers can depend on its identity.
 */
export function useSelectionView(): SelectionView {
  const selection = useGridStore((s) => s.selection);
  const grid = useGridStore((s) =>
    selection ? s.grids[selection.gridId] : undefined,
  );
  return useMemo(
    () =>
      classifySelection(
        selection && grid ? { [selection.gridId]: grid } : {},
        selection,
      ),
    [selection, grid],
  );
}

/** Human name for a widget kind — the bar's selection label. Two kinds read
 *  as their own widget once configured: a button with a link, an input with
 *  an icon. */
export function widgetKindName(
  contentType: string,
  contentProps?: Record<string, unknown>,
): string {
  switch (contentType) {
    case "button":
      return typeof contentProps?.["href"] === "string" &&
        contentProps["href"] !== ""
        ? "Link button"
        : "Button";
    case "input":
      return contentProps?.["icon"] ? "Input group" : "Input";
    case "grid":
      return "Section";
    case "inline-grid":
      return "Inline";
    case "text":
      return "Text";
    case "image":
      return "Image";
    case "video":
      return "Video";
    case "audio":
      return "Audio";
    case "form":
      return "Form";
    case "nav":
      return "Menu";
    case "spacer":
      return "Spacer";
    default:
      return contentType.charAt(0).toUpperCase() + contentType.slice(1);
  }
}

/** The kind-specific primary verb for a widget, or null when the kind has no
 *  content to drill into (spacer, menu). Media kinds vary on whether they
 *  already hold a file: the first act on an empty tile is to fill it. */
export function editVerbFor(
  contentType: string,
  contentProps: Record<string, unknown> | undefined,
): string | null {
  const hasMedia = !!(contentProps?.["sha256"] || contentProps?.["url"]);
  switch (contentType) {
    case "text":
    case "heading":
      return "Edit text";
    case "image":
      return hasMedia ? "Crop" : "Add image";
    case "video":
      return hasMedia ? "Replace" : "Add video";
    case "audio":
      return hasMedia ? "Replace" : "Add audio";
    default:
      return null;
  }
}
